import { Classroom, Group, RankDetail, StudentCertificates } from '../../api/queries/certificate-reporting/api-types';
import { getCertificatesCount } from '../../components/result-grid/__helpers__/result-grid-helper';
import { getStudentIDsAtGroupLevel } from '../../containers/filter-container/__helpers__/filter-helper';
import { CERTIFICATE_ROW_VARIANT, RANK_TYPE } from '../../utils/enums';
import { EXPORT_LEVELS, PAST_EXPORT_LEVELS } from './export-consts';

export type ExportRowType = CERTIFICATE_ROW_VARIANT | 'total';

export interface ExportRow {
    type: ExportRowType
    name: string
    counts: number[]
    total: number
}

export interface BuildExportRowsArgs {
    classes: Classroom[]
    selectedGroupId: number
    studentCertificates: StudentCertificates[]
}

const countsFor = (studentIds: string[], studentCertificates: StudentCertificates[]) => {
    const detail = getCertificatesCount(
        Array.from(new Set(studentIds)),
        studentCertificates
    );

    return {
        counts: EXPORT_LEVELS.map((level) => detail[level].count),
        total: detail.total.count
    };
};

const groupsInScope = (classroom: Classroom, selectedGroupId: number): Group[] => (
    selectedGroupId === 0
        ? classroom.groups
        : classroom.groups.filter((group) => group.id === selectedGroupId)
);

export function buildExportRows({
    classes,
    selectedGroupId,
    studentCertificates
}: BuildExportRowsArgs): ExportRow[] {
    const rows: ExportRow[] = [];
    const allStudentIds: string[] = [];    const scopedClasses = classes.filter(
        (classroom) => groupsInScope(classroom, selectedGroupId).length > 0
    );
    const showClassRows = scopedClasses.length > 1;

    scopedClasses.forEach((classroom) => {
        const groups = groupsInScope(classroom, selectedGroupId);
        const classStudentIds = groups.flatMap(getStudentIDsAtGroupLevel);

        allStudentIds.push(...classStudentIds);

        if (showClassRows) {
            rows.push({
                type: CERTIFICATE_ROW_VARIANT.class,
                name: classroom.name,
                ...countsFor(classStudentIds, studentCertificates)
            });
        }

        const showGroupRows = groups.length > 1;

        groups.forEach((group) => {
            if (showGroupRows) {
                rows.push({
                    type: CERTIFICATE_ROW_VARIANT.group,
                    name: group.name,
                    ...countsFor(getStudentIDsAtGroupLevel(group), studentCertificates)
                });
            }

            group.students.forEach((student) => {
                rows.push({
                    type: CERTIFICATE_ROW_VARIANT.student,
                    name: student.name,
                    ...countsFor([student.userId], studentCertificates)
                });
            });
        });
    });

    rows.push({
        type: 'total',
        name: '',
        ...countsFor(allStudentIds, studentCertificates)
    });

    return rows;
}

export type PastCertificateTotals = Record<RANK_TYPE.bronze | RANK_TYPE.silver | RANK_TYPE.gold, RankDetail>;

export function buildPastExportRows(pastCertificates: PastCertificateTotals): ExportRow[] {
    const counts = PAST_EXPORT_LEVELS.map((level) => pastCertificates[
        level as keyof PastCertificateTotals
    ]?.count ?? 0);

    return [{
        type: 'total',
        name: '',
        counts,
        total: counts.reduce((sum, count) => sum + count, 0)
    }];
}
